import { createSlice } from '@reduxjs/toolkit'
import { getRuntimeErrorMessage } from '../../lib/runtimeErrorMessages'

function findStreamingIndex(transcript, userText) {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const entry = transcript[i]
    if (entry?.isStreaming && entry?.userText === userText) return i
  }
  return -1
}

export const botSlice = createSlice({
  name: 'bot',
  initialState: {
    currentSession: null,
    currentTranscript: [],
    sessions: [],
    pendingResponse: false
  },
  reducers: {
    currentUpdated: (state, action) => {
      return {
        ...state,
        currentSession: action.payload.currentSession,
        currentTranscript: action.payload.currentTranscript,
      }
    },
    sessionsUpdated: (state, action) => {
      return {
        ...state,
        sessions: action.payload.sessions,
        currentSession: action.payload.currentSession,
        currentTranscript: action.payload.currentTranscript,
      }
    },
    messagePosted: (state, action) => {
      return {
        ...state,
        currentTranscript: action.payload.currentTranscript,
        pendingResponse: true
      }
    },
    startStreamingResponse: (state, action) => {
      const { userText } = action.payload || {}
      if (!userText) return

      state.currentTranscript.push({
        sender: 'MODEL',
        text: '',
        isStreaming: true,
        streamThinking: '',
        userText,
      })
    },
    appendStreamDelta: (state, action) => {
      const { userText, streamType, text } = action.payload || {}
      if (!userText || !text) return

      const index = findStreamingIndex(state.currentTranscript, userText)
      if (index === -1) return

      const entry = state.currentTranscript[index]
      entry.isStreaming = true
      if (streamType === 'thinking_delta') {
        entry.streamThinking = (entry.streamThinking || '') + text
      } else if (streamType === 'message_delta') {
        entry.text = (entry.text || '') + text
      }
    },
    resetStreamBuffers: (state, action) => {
      const { userText } = action.payload || {}
      if (!userText) return

      const index = findStreamingIndex(state.currentTranscript, userText)
      if (index === -1) return

      const entry = state.currentTranscript[index]
      entry.isStreaming = true
      entry.streamThinking = ''
      entry.text = ''
    },
    finalizeStreamingResponse: (state, action) => {
      const { userText, modelMessage } = action.payload || {}
      if (!userText) return

      const index = findStreamingIndex(state.currentTranscript, userText)
      if (index === -1) return

      const entry = state.currentTranscript[index]
      if (modelMessage) {
        entry.text = modelMessage
      }
      delete entry.isStreaming
      delete entry.streamThinking
      delete entry.userText
      state.pendingResponse = false
    },
    markStreamingFailed: (state, action) => {
      const { userText, errorCode, source } = action.payload || {}
      if (!userText) return

      const index = findStreamingIndex(state.currentTranscript, userText)
      if (index === -1) return

      const entry = state.currentTranscript[index]
      entry.errorMessage = getRuntimeErrorMessage(errorCode, source)
      delete entry.isStreaming
      delete entry.streamThinking
      delete entry.userText
      state.pendingResponse = false
    },
    responseReceived: (state, action) => {
      return {
        ...state,
        currentTranscript: action.payload.currentTranscript,
        pendingResponse: false
      }
    },
    sessionNameUpdated: (state, action) => {
      const { id, name } = action.payload || {}
      if (!id || name == null) return
      const session = state.sessions.find((s) => s.id === id)
      if (session) session.name = name
    },
  },
})

export const {
  currentUpdated,
  sessionsUpdated,
  messagePosted,
  startStreamingResponse,
  appendStreamDelta,
  resetStreamBuffers,
  finalizeStreamingResponse,
  markStreamingFailed,
  responseReceived,
  sessionNameUpdated,
} = botSlice.actions

export default botSlice.reducer
